import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { IntlProvider } from "react-intl";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import Home from "pages/Home/Home";
import Login from "pages/Login/Login";
import LoginCustomer from "pages/Login/LoginCustomer";
import Docs from "pages/Docs/index";
import Dash from "pages/Dash/Dash";
import Apps from "pages/Apps/Apps";
import NotFound from "pages/NotFound";
import { PublicOnly, RequireAuth } from "pages/RouteGuards";
import Profile from "pages/Dash/Profile";
import { ThemeContext } from "theme/Context";
import { theme } from "theme/theme";
import useLangStore from "store/langStore";

import { setStorage, getStorage } from "store/local";
import getRoutes from "models/routes";
import proxy from "api/proxy";
import env from "./env";
import BreakpointIndicator from "components/Tools/BreakpointIndicator";

const queryClient = new QueryClient();

function MainApp(props) {
  // document.body.classList.add("dark");
  // document.body.classList.toggle("dark");
  document.body.classList.toggle("light");
  const { locale, messages } = useLangStore();
  const sessionStart = getStorage("ctxSession");

  const [sessionAuth, setSessionAuth] = useState(sessionStart);
  const [sessionNotAuth, setSessionNotAuth] = useState(null);
  const [routesDash, setRoutesDash] = useState(null);
  const [menusDash, setMenusDash] = useState(null);
  const [routesApps, setRoutesApps] = useState(null);

  function handleSession(session) {
    setSessionAuth(session);
    setDashRoutes(session);
  }

  async function setDashRoutes(session) {
    const { menus, routesDash } = await getRoutes(session);
    setMenusDash(menus);
    setRoutesDash(routesDash);
  }

  async function getSessionApp() {
    const [, side, db] = location.pathname.split("/");
    if (side !== "app") {
      return;
    }
    const { data } = await proxy.search(
      "res.user",
      [["login", "=", "user.app"]],
      ["name", "company", "company.currency"],
    );

    const [user] = data;
    const session = {
      db: db,
      user: user.id,
      user_name: "user.app",
      company: 1,
      currency: user["company."].currency,
      session: env.api_key,
    };
    await setStorage("ctxVirtualSession", session);
    return session;
  }

  useEffect(() => {
    async function setSession() {
      const sessionApp = await getSessionApp();
      if (sessionApp) {
        setSessionNotAuth(sessionApp);
        const { routesApps } = await getRoutes(sessionApp);
        setRoutesApps(routesApps);
      }
      if (sessionAuth) {
        setDashRoutes(sessionAuth);
      }
    }
    setSession();
  }, [props]);

  // Soon we add theme management and dark mode with this Component
  return (
    <ThemeContext.Provider accentColor="mint" value={theme}>
      <QueryClientProvider client={queryClient}>
        <IntlProvider locale={locale} messages={messages} defaultLocale="en">
          <BrowserRouter basename="/">
            <div id="main-app" className="h-full w-full">
              <Routes>
                <Route
                  path="/"
                  element={
                    <PublicOnly>
                      <Home />
                    </PublicOnly>
                  }
                />
                <Route
                  path="/login"
                  element={
                    <PublicOnly>
                      <Login handleSession={handleSession} />
                    </PublicOnly>
                  }
                />
                <Route
                  path="/loginCustomer"
                  element={
                    <PublicOnly>
                      <LoginCustomer handleSession={handleSession} />
                    </PublicOnly>
                  }
                />
                <Route path="/dash">
                  <Route
                    path="*"
                    element={
                      <RequireAuth>
                        <Dash menusDash={menusDash} />
                      </RequireAuth>
                    }
                  >
                    {routesDash}
                    <Route path="profile" element={<Profile />} />
                  </Route>
                </Route>
                <Route path="/docs" element={<Docs />} />
                <Route
                  path="/app/:db/*"
                  element={<Apps session={sessionNotAuth} />}
                >
                  {routesApps}
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </BrowserRouter>
        </IntlProvider>
      </QueryClientProvider>
      <BreakpointIndicator />
    </ThemeContext.Provider>
  );
}

export default MainApp;
